import React, { useState } from "react";
import { Coins, Clock, Target, Trophy, Zap, Volume2, VolumeX } from "lucide-react";
import { isMuted, toggleMuted } from "./sound";
import PlayerCountBadge from "../components/PlayerCountBadge";

export default function HUD({
  level,
  zombiesCollected,
  goal,
  coinsEarned,
  score,
  timeLeft,
  beamActive,
  beamCharge,
  beamTimeLeft = 10,
  beamMaxHold = 10,
  beamLocked = false,
  bossActive,
  bossHp,
  onPause,
  touchMode = false,
}) {
  const [muted, setMutedState] = useState(() => isMuted());
  const handleMute = () => {
    const next = toggleMuted();
    setMutedState(next);
  };
  const pct = Math.min(100, (zombiesCollected / goal) * 100);
  const timePct = Math.max(0, (timeLeft / level.time) * 100);
  const timeUrgent = timeLeft < 15;

  return (
    <div className={`hud-root ${touchMode ? "touch" : ""}`} data-testid="game-hud">
      {/* Top bar */}
      <div className="hud-top">
        <div className="hud-card" data-testid="hud-level">
          <div className="hud-card-label">
            {level.wave && level.wave > 1 ? `Wave ${level.wave} · ` : ""}Level {level.id}
          </div>
          <div className="hud-card-value">{level.name}</div>
        </div>

        <div className="hud-card hud-card-time" data-cls={timeUrgent ? "urgent" : ""}>
          <div className="hud-card-label">
            <Clock size={12} /> Time
          </div>
          <div className={`hud-card-value ${timeUrgent ? "urgent" : ""}`} data-testid="hud-timer">
            {Math.ceil(timeLeft)}s
          </div>
          <div className="hud-bar">
            <div
              className={`hud-bar-fill ${timeUrgent ? "urgent" : ""}`}
              style={{ width: `${timePct}%` }}
            />
          </div>
        </div>

        <div className="hud-card" data-testid="hud-score">
          <div className="hud-card-label">
            <Trophy size={12} /> Score
          </div>
          <div className="hud-card-value">{score}</div>
        </div>

        <button
          type="button"
          onClick={handleMute}
          className={`mute-btn ${muted ? "muted" : ""}`}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="mute-button"
          title={muted ? "Sound off" : "Sound on"}
          style={{ position: "static" }}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>

        <button
          type="button"
          onClick={onPause}
          className="hud-pause-btn"
          data-testid="pause-button"
        >
          Pause
        </button>
      </div>

      {/* Center top - goal progress */}
      <div className="hud-goal">
        <div className="hud-goal-row">
          <Target size={14} />
          <span data-testid="hud-progress">
            {zombiesCollected} / {goal} zombies
          </span>
          {level.boss && (
            <span className="hud-boss-tag">
              {bossActive ? `Boss HP ${Math.ceil(bossHp)}` : "Boss incoming"}
            </span>
          )}
        </div>
        <div className="hud-progress-bar">
          <div className="hud-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Bottom left coins */}
      <div className="hud-coins" data-testid="hud-coins">
        <Coins size={16} />
        <span>+{coinsEarned}</span>
      </div>

      {/* Live concurrent-player badge (heartbeats while in-game) */}
      <PlayerCountBadge active variant="mini" />

      {/* Bottom right beam status */}
      <div
        className={`hud-beam ${beamActive ? "active" : ""} ${beamLocked ? "locked" : ""}`}
        data-testid="hud-beam-status"
      >
        <Zap size={14} />
        <span data-testid="hud-beam-label">
          {beamLocked
            ? "Beam: release to reset"
            : beamActive
            ? `Beam: ${beamTimeLeft.toFixed(1)}s`
            : `Beam: ${beamTimeLeft.toFixed(0)}s`}
        </span>
        <div className="hud-beam-bar">
          <div
            className={`hud-beam-fill ${beamLocked ? "locked" : ""}`}
            style={{ width: `${(beamTimeLeft / beamMaxHold) * 100}%` }}
            data-testid="hud-beam-fill"
          />
        </div>
      </div>

      {/* Controls hint */}
      <div className="hud-controls-hint">
        <kbd>WASD</kbd>
        <span>or</span>
        <kbd>mouse</kbd>
        <span>move</span>
        <kbd>SPACE</kbd>
        <span>or</span>
        <kbd>click</kbd>
        <span>beam</span>
      </div>
    </div>
  );
}
